import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { Country, State, City } from "country-state-city";
import { handleApiErrorResponses } from "../utils/ApisErrorHandling";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import DoctorDetailsForm from "./DoctorDetailsForm";
import { HospitalServices } from "../services/HospitalServices";
import { AuthServices } from "../services/AuthServices";
import CloseIcon from "../assets/crossIcon.svg";
import "./Adddoctor.scss";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import { emailRegex } from "../utils/formUtils";

const doctorDetailsSchema = Yup.object({
  email: Yup.string().matches(emailRegex),
  phone: Yup.string()
    .nullable()
    .max(10, "Phone number must be 10 characters long"),
  birthDate: Yup.string().nullable(),
  address: Yup.string(),
  country: Yup.string(),
  state: Yup.string(),
  city: Yup.string(),
  pincode: Yup.string().nullable(),
  highestQualification: Yup.string(),
  gender: Yup.string().required("Required"),
  hospitalId: Yup.string().required("Required"),
  fromDate: Yup.string().nullable(),
  doctorType: Yup.string().required("Required"),
  fullName: Yup.string()
    .matches(/^[a-zA-Z\s-]*$/, "Full name accept letters only")
    .min(3, "Name must be greater than 3 characters long")
    .max(30, "FullName should not contain more than 30 characters")
    .required("Full Name is required"),

  registrationNumber: Yup.string().matches(/^\d+$/, "Only digits are allowed"),
});

const AddDoctor = () => {
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [hospitalList, setHospitalList] = useState([]);
  const [specialityList, setSpecialityList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [documentCertificate, setDocumentCertificate] = useState(null);
  const [viewImage, setViewImage] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const initialValues = {
    fullName: "",
    email: "",
    countryCode: "91",
    phone: null,
    birthDate: null,
    address: "",
    country: "",
    state: "",
    city: "",
    pincode: null,
    qualifications: "",
    highestQualification: "",
    specialityIds: [],
    gender: "",
    hospitalId: "",
    iCardNo: "",
    fromDate: null,
    doctorType: "",
    certificateUrl: null,
  };
  const doctorTypeOption = [
    { value: "Duty Doctor", label: "Duty Doctor" },
    { value: "Specialist", label: "Specialist" },
    { value: "Paramedic", label: "Paramedic" },
    { value: "Anaesthist", label: "Anaesthist" },
    { value: "Consultant", label: "Consultant" },
    { value: "Visiting Consultant", label: "Visiting Consultant" },
    { value: "Others", label: "Others" },
  ];

  const navigate = useNavigate();

  const handleSubmit = async (values) => {
    if (documentCertificate) {
      values.certificateUrl = documentCertificate;
    }
    setIsSubmitting(true);
    try {
      const response = await HospitalServices.addDoctor(values);
      window.alert(response.data.message);
      navigate("/dashboard/doctorlist");
    } catch (error) {
      handleApiErrorResponses(error);
    }
    setIsSubmitting(false);
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const uploadImage = async (e) => {
    setIsUploading(true);

    if (e.target.files[0].size > 5242880) {
      window.alert("Please upload a file smaller than 2 MB");
      setIsUploading(false);
    } else {
      try {
        let data = new FormData();
        data.append("image", e.target.files[0]);

        let res = await AuthServices.uploadImage(data);
        setDocumentCertificate(res.data.url);
      } catch (error) {
        handleApiErrorResponses(error);
      }

      setIsUploading(false);
    }
  };

  const getHospitalList = async () => {
    try {
      const response = await HospitalServices.gethospitals("", 1, 1500);
      setHospitalList(response.data.list);
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  const getUniqueSpecialityList = async () => {
    try {
      const response = await HospitalServices.getUniqueSpecialityList(
        "",
        1,
        40
      );
      setSpecialityList(response.data.list);
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  useEffect(() => {
    getHospitalList();
    getUniqueSpecialityList();
  }, []);

  useEffect(() => {
    setStateList(State?.getStatesOfCountry(selectedCountry?.isoCode));
  }, [selectedCountry]);

  useEffect(() => {
    setCityList(
      City.getCitiesOfState(selectedState?.countryCode, selectedState?.isoCode)
    );
  }, [selectedState]);

  return (
    <div className="p-[32px] mt-[92px] w-full">
      <span className="path_line">
        <WidgetsOutlinedIcon style={{ height: "24px" }} /> &nbsp; /
        &nbsp;Profile &nbsp; / &nbsp; Add Doctor
      </span>
      <div className="heading"> ADD DOCTOR</div>
      <hr
        style={{ width: "100%", border: "1px solid rgba(228, 228, 228, 1)" }}
      />
      <div
        style={{ border: "1px solid #e4e4e4", marginTop: "24px" }}
        className="rounded-[16px] p-5"
      >
        <DoctorDetailsForm
          initialValues={initialValues}
          doctorDetailsSchema={doctorDetailsSchema}
          doctorTypeOption={doctorTypeOption}
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          stateList={stateList}
          cityList={cityList}
          selectedCountry={selectedCountry}
          selectedState={selectedState}
          selectedCity={selectedCity}
          setSelectedCountry={setSelectedCountry}
          setSelectedState={setSelectedState}
          setSelectedCity={setSelectedCity}
          hospitalList={hospitalList}
          specialityList={specialityList}
          isUploading={isUploading}
          uploadImageHandler={uploadImage}
          documentCertificate={documentCertificate}
          handleButtonClick={handleButtonClick}
          viewImage={viewImage}
          setViewImage={setViewImage}
          fileInputRef={fileInputRef}
        />
      </div>

      {viewImage && (
        <div className="fixed top-0 left-0 h-screen w-screen flex justify-center items-center bg-[#017C5789] z-[1201]">
          <div className="relative w-[90%] h-[90%] bg-white flex justify-center items-center rounded-[20px] ">
            <div
              className="absolute top-3 right-3 cursor-pointer"
              onClick={() => setViewImage(false)}
            >
              <img src={CloseIcon} alt="close" />
            </div>
            <div className="h-[100%] w-[100%] flex justify-center overflow-y-auto p-5">
              <img
                src={documentCertificate}
                alt="uploaded-certificate"
                className="cursor-zoom-out"
                onClick={() => setViewImage(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddDoctor;
